/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import CampaignModel, {
  AudienceSettingsType,
  CampaignRestartModes,
  CampaignType,
  FormTriggeredCampaignAttributeFilters,
  ObjectRelationshipTriggerType,
  ObjectTriggerType,
  TRIGGER_CATEGORIES_NAMES_FOR_TRACKING,
  TriggerCategory,
} from 'cio/models/campaign';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import AccountService from 'cio/services/account';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import cloneDeep from 'lodash.clonedeep';
import { debounce } from '@ember/runloop';
import config from 'cio/config/environment';
import { EventTriggeredCampaignAttributeFilters } from 'cio/models/campaign';
import { CampaignsTriggerSetupWebhookArgs } from 'cio/components/campaigns/trigger/setup/condition-blocks/webhook';
import { FrequencyOption } from 'cio/utils/campaigns/date-triggered';
import pluralize from 'cio/utils/pluralize';
import DS from 'ember-data';
import GroupCondition from 'cio/app-models/conditions/group';
import { isGroupCondition } from 'cio/utils/conditions/serialize';
import CustomForm from 'cio/models/custom-form';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CustomFormsService from 'cio/services/custom-forms';
import {
  EventAndFormFrequencyOptions,
  ObjectAndRelationshipFrequencyOptions,
  SegmentFrequencyOptions,
  getSelectedFrequencyOption,
} from 'cio/components/campaigns/trigger/setup/frequency-controls';
import ObjectTypeModel from 'cio/models/object-type';
import ObjectTypesService from 'cio/services/object-types';
import { ObjectAttributesKey } from 'cio/components/campaigns/trigger/setup/condition-blocks/object/config';
import { RelationshipAttributesKey } from 'cio/components/campaigns/trigger/setup/condition-blocks/relationship/config';
import AnalyticsService from 'cio/services/analytics';
import { findOperator } from 'cio/utils/conditions-operators';
import article from 'cio/utils/a';
import {
  deserializeEventAttributeFilters,
  deserializeFormAttributeFilters,
} from 'cio/pods/campaigns/campaign/setup/trigger/index/controller';
import { getFullAttributeValue } from 'cio/utils/conditions/attribute-value';
import { ExitCondition, selectedExitCondition } from 'cio/utils/campaigns/exit-conditions';
import { capitalize } from '@ember/string';

/* prettier-ignore */
const TriggerCategories: { label: string; category: string; icon: string; }[] = [
  { label: 'All Triggers', category: TriggerCategory.all, icon: 'trigger' },
  { label: 'People', category: TriggerCategory.people, icon: 'person' },
  { label: 'Objects', category: TriggerCategory.objects, icon: 'object' }, // will be used as a template for all object types
  { label: 'Dates', category: TriggerCategory.dates, icon: 'trigger-date' },
  { label: 'Webhooks', category: TriggerCategory.webhooks, icon: 'webhook' },
];

enum FilterType {
  Person = 'Person',
  Relationship = 'Relationship',
  Object = 'Object',
}

export interface TriggerCardItem {
  id: string;
  subType?: ObjectTriggerType | ObjectRelationshipTriggerType;
  icon: string;
  label: string;
  description: string;
  isFaded?: boolean;
  objectTypeId?: string;
  secondaryIcon?: string;
  secondaryFaded?: boolean;
  tags?: string[];
  trackingValue?: string;
  info?: string;
}
/* prettier-ignore */
const TriggersByCategory: Record<string, TriggerCardItem[]> = {
  [TriggerCategory.recentlyUsed]: [], // this is populated by API
  [TriggerCategory.people]: [
    { id: CampaignType.Behavioral, icon: 'segment', label: 'Segment change', description: 'A person enters or leaves a specific segment.', tags: ['people', 'customer'], info: '<p>The <strong>segment change</strong> trigger starts the campaign when a person enters or exits a segment. You can pick an existing segment or set criteria for a new segment.</p> <p>Common use cases:</p> <ul><li>Onboarding drip campaigns</li><li>Inactivity reminders</li><li>NPS surveys</li></ul>'},
    { id: CampaignType.Transactional, icon: 'event', label: 'Event', description: 'A person performs an event in your product.', tags: ['people', 'customer'], info: '<p>The <strong>event</strong> trigger starts the campaign when a person performs an event you track. You can use this to monitor and respond to activity on your website, app, or product.</p> <p>Common use cases:</p> <ul><li>Feature adoption campaigns</li><li>Messaging based on behaviors</li><li>Abandoned cart reminders</li></ul>'},
    { id: CampaignType.Form, icon: 'form', label: 'Form submission', description: 'A person submits a form.', tags: ['people', 'customer'], info: '<p>The <strong>form submission</strong> trigger starts the campaign when a person submits a specific form. You can use information submitted in the form in the messages you send.</p> <p>Common use cases:</p> <ul><li>Sales or demo requests</li><li>Event registrations</li><li>Research surveys</li></ul>'},
  ],
  [TriggerCategory.objects]: [ // will be used as a template for all object & relationship types
  { id: CampaignType.Object, subType: ObjectTriggerType.Updated, icon: 'company', label: '{singularName} updated', description: '{singularName} is updated.', tags: ['objects', 'changed', 'updated'], trackingValue: 'Object Updated'},
  // TODO: uncomment both "CampaignType.Object" types for Relationship Campaigns [Slice 3]
    // { id: CampaignType.Object, subType: ObjectTriggerType.Deleted, icon: 'company', label: '{singularName} deleted', description: 'A {singularName} is deleted.', tags: ['objects', 'removed', 'deleted'], isFaded: true, trackingValue: 'Object Deleted' },
    { id: CampaignType.ObjectRelationship, subType: ObjectRelationshipTriggerType.Created, icon: 'company', label: 'Person added', description: 'A person is added to {singularName}.', secondaryIcon: 'person', tags: ['object', 'people', 'customer', 'created', 'added', 'relationship'], trackingValue: 'Relationship Created', info: '<p>The <strong>person added</strong> trigger starts the campaign when a relationship is established between a person and a custom object. You can use data about the person, relationship, and custom object in the messages you send.</p> <p>Common use cases:</p> <ul><li>Onboarding users invited to accounts</li><li>Messaging people that enrolled in a course</li><li>Managing applicants for a job</li></ul>'},
    { id: CampaignType.ObjectRelationship, subType: ObjectRelationshipTriggerType.Updated, icon: 'company', label: 'Relationship changed', description: 'A relationship attribute is changed.', secondaryIcon: 'person', tags: ['object', 'people', 'customer', 'updated', 'changed', 'relationship'], trackingValue: 'Relationship Changed', info: '<p>The <strong>relationship changed</strong> trigger starts the campaign when a relationship between a person and a custom object changes. This can be an existing attribute that\'s updated or a new attribute that\'s added. You can use data about the person, relationship, and custom object in the messages you send.</p> <p>Common use cases:</p> <ul><li>Onboarding users with increased permissions</li><li>Expired subscription notifications</li></ul>'},
    // TODO: uncomment for Relationship Campaigns [Slice 3]
    // { id: CampaignType.ObjectRelationship, subType: ObjectRelationshipTriggerType.Deleted, icon: 'company', label: 'Person removed', description: 'A person is removed from a {singularName}.', secondaryIcon: 'person', secondaryFaded: true, tags: ['object', 'people', 'customer', 'removed', 'deleted', 'relationship'], trackingValue: 'Relationship Deleted', info: '<p>The <strong<person removed</strong> trigger starts the campaign when there is no longer a relationship between a person and a custom object. You can use data about the person, relationship, and custom object in the messages you send.</p> <p>Common use cases:</p> <ul><li>Internal notifications</li><li>Updating people attributes</li></ul>'},
  ],
  [TriggerCategory.dates]: [
    { id: CampaignType.Date, icon: 'trigger-date', label: 'Important date', description: 'Something is planned for a specific date.', info: '<p>The <strong>important date</strong> trigger starts the campaign using a specific date you\'ve stored as an attribute. You can use the exact date or adjust it to trigger before or after.</p> <p>Common use cases:</p> <ul><li>Subscription renewals</li><li>Payment reminders</li><li>Birthdays or anniversaries</li></ul>'}
  ],
  [TriggerCategory.webhooks]: [
    { id: CampaignType.Webhook, icon: 'webhook', label: 'Webhook', description: 'Send a webhook request from a different app.', info: '<p>The <strong>webhook</strong> trigger starts the campaign when data is received from an external product or service. Data is received in JSON format, and you can use that data in Customer.io to message customers, create custom events, update attributes, and <a href="https://customer.io/docs/journeys/webhook-triggered-campaigns/" target="_blank" rel="noopener noreferrer">much more</a>.</p> <p>Common use cases:</p> <ul><li>Payment received in Stripe</li><li>Support ticket created in Zendesk</li><li>Contact or deal created in HubSpot</li></ul>'},
  ],
};

interface TriggerTypeSelectorArgs extends CampaignsTriggerSetupWebhookArgs {
  onChangeType: (
    type: CampaignType,
    objectTypeId?: string,
    subType?: ObjectTriggerType | ObjectRelationshipTriggerType,
    trackingValue?: string,
  ) => void;
  selectedType: CampaignType;
  resetSelectedType: () => void;
  triggerIsSaved: boolean;
  attributeFilters: EventTriggeredCampaignAttributeFilters | FormTriggeredCampaignAttributeFilters;
  shouldShowFrequencyControls: boolean;
  shouldShowFilterControls: boolean;
  objectCampaign?: CampaignModel['objectCampaign'];
  relationshipCampaign?: CampaignModel['relationshipCampaign'];
  objectType?: ObjectTypeModel;
  objectTypeId?: number;
  objectAttributeTriggers?: CampaignModel[ObjectAttributesKey.objectAttributeTriggers];
  objectFilters?: CampaignModel[ObjectAttributesKey.objectFilters];
  relationshipAttributeTriggers?: CampaignModel['relationshipAttributeTriggers'];
  relationshipFilters?: CampaignModel['relationshipFilters'];
  audience?: CampaignModel['audience'];
  updateObjectAttributeConditions: (key: ObjectAttributesKey, filters: CampaignModel[ObjectAttributesKey]) => unknown;
  toggleFeedbackModal: () => unknown;
  updateTopLevelValue: (key: string, value: any) => unknown;
  updateTopLevelValues: (values: Record<string, any>) => unknown;
  anchors: Record<string, unknown>[];
  filters: Record<string, unknown>[];
  restartMode: CampaignRestartModes;
  exitOnTriggerOrFilterNotMatched: boolean;
  exitOnConversionMatched: boolean;
  // TODO: specify types
  areFrequencyControlsDisabled: any;
  onUpdateRestartMode: any;
  onUpdateInterval: any;

  // fixed in next PR due to condition block config components
  [key: string]: any;
}

export default class TriggerTypeSelector extends Component<TriggerTypeSelectorArgs> {
  @service account!: AccountService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;
  @service store!: DS.Store;
  @service objectTypes!: ObjectTypesService;
  @service analytics!: AnalyticsService;

  CampaignType = CampaignType;
  @tracked labels: Record<string, string> = {};
  @tracked categories: typeof TriggerCategories = [];
  @tracked selectedCategory: string = TriggerCategory.recentlyUsed;
  @tracked selectedTypeName = '';
  triggersByCategory: typeof TriggersByCategory = {}; // internal usage, for search
  @tracked triggersByCategoryFiltered: typeof TriggersByCategory = {};
  @tracked searchTerm = '';
  @tracked showConditionResetConfirmation = false;
  @tracked displayedFilters = {
    [FilterType.Person]: false,
    [FilterType.Relationship]: false,
    [FilterType.Object]: false,
  };
  @tracked showAllSummaries = false;
  @tracked showRemoveFiltersConfirmation = false;

  readonly recentlyUsedType = { label: 'Recently Used', category: TriggerCategory.recentlyUsed, icon: 'time-window' };
  readonly ObjectAttributesKeys = ObjectAttributesKey;
  readonly RelationshipAttributesKeys = RelationshipAttributesKey;
  readonly maxColumnCountBeforeThreshold = 3; // we display max 3 blocks of segment conditions, if more than threshold, then we show "threshold minus 1" segments and "x more"
  readonly FilterTypes = FilterType;

  supportAgentImage = new URL('cio/assets/images/support-agent.png', import.meta.url).toString();
  triggerSummaryImage = new URL('cio/assets/images/trigger-summary.svg', import.meta.url).toString();

  constructor(owner: unknown, args: TriggerTypeSelectorArgs) {
    super(owner, args);
    this.#setupObjectTypesCategories();
    this.#setupDisplayedFilters();
  }

  get hasTriggerConditions() {
    let {
      anchors,
      filters,
      objectAttributeTriggers,
      objectFilters,
      relationshipAttributeTriggers,
      relationshipFilters,
      dateTriggeredAttribute,
      event,
      selectedType,
    } = this.args;

    return [
      anchors,
      filters,
      objectAttributeTriggers,
      objectFilters,
      relationshipAttributeTriggers,
      relationshipFilters,
      dateTriggeredAttribute, // checking only one date related field, because this one is always required
      event, // is also used for forms
    ].some((item) => {
      if (selectedType === CampaignType.Form) {
        return this.summary.length > 0;
      }

      return item != null && item.length > 0;
    });
  }

  #setupObjectTypesCategories() {
    let recentTriggers = this.store
      .peekAll('recent-trigger')
      .toArray()
      .filter((rt) => rt.isValidForCampaignTrigger);
    let { selectedType } = this.args;
    let { enabledObjectTypes } = this.objectTypes;
    this.labels = {
      recentlyUsed: 'Recently Used',
    };
    this.categories = TriggerCategories.reduce<typeof TriggerCategories>((acc, item) => {
      if (item.category === TriggerCategory.objects) {
        enabledObjectTypes.map((objectType) => {
          let { name, id, objectIcon } = objectType;
          acc.push({
            icon: objectIcon,
            label: name,
            category: `object_type_${id}`,
          });
          this.labels[`object_type_${id}`] = name;
        });
      } else {
        acc.push(item);
        this.labels[item.category] = item.label;
      }
      return acc;
    }, []);
    let triggersByCategory = Object.entries(TriggersByCategory).reduce<typeof TriggersByCategory>(
      (acc, [category, items]) => {
        if (category === TriggerCategory.recentlyUsed && recentTriggers.length > 0) {
          return acc;
        } else if (category === TriggerCategory.objects) {
          enabledObjectTypes.map((objectType) => {
            let { singularName, id, objectIcon, name } = objectType;
            let objectItems = items.map((item) => ({
              ...item,
              label: item.label.replace('{singularName}', singularName),
              description: item.description.replace('{singularName}', article(singularName.toLowerCase())),
              objectTypeId: id,
              icon: objectIcon,
              tags: [...(item.tags ?? []), singularName, name],
            }));
            acc[`object_type_${id}`] = objectItems;
            if (!isEmpty(selectedType)) {
              let subType = this.objectOrRelationshipCampaignSubtype;
              let matchedType =
                objectItems.find((item) => item.id === selectedType && item.subType === subType)?.label ?? '';
              if (!isEmpty(matchedType)) {
                this.selectedTypeName = matchedType;
              }
            }
          });
        } else {
          acc[category] = [...items];
          if (!isEmpty(selectedType)) {
            let matchedType = items.find((item) => item.id === selectedType)?.label ?? '';
            if (!isEmpty(matchedType)) {
              this.selectedTypeName = matchedType;
            }
          }
        }

        return acc;
      },
      {},
    );
    if (recentTriggers.length > 0) {
      triggersByCategory[TriggerCategory.recentlyUsed] = [...recentTriggers]
        .map((rt) => {
          return triggersByCategory[rt.campaignTriggerCategory]?.find(
            (item) =>
              rt.type === item.id &&
              Number(rt.objectTypeId ?? 0) === Number(item.objectTypeId ?? 0) &&
              Number(rt.triggerType ?? 0) === Number(item.subType ?? 0),
          );
        })
        .filter((item) => item != null) as TriggerCardItem[];
    } else {
      this.selectedCategory = TriggerCategory.all;
    }
    this.triggersByCategory = triggersByCategory;
    this.filterTypesForSearch();
  }

  #setupDisplayedFilters() {
    let { shouldShowFilterControls, filters, relationshipFilters, objectFilters, objectType } = this.args;
    let displayedFilters = {
      [FilterType.Person]: false,
      [FilterType.Relationship]: false,
      [FilterType.Object]: false,
    };

    if (shouldShowFilterControls === true) {
      if (filters?.length > 0) {
        displayedFilters[FilterType.Person] = true;
      }
      if (this.isObjectCampaignType && objectType != null) {
        if ((relationshipFilters?.length ?? 0) > 0) {
          displayedFilters[FilterType.Relationship] = true;
        }
        if ((objectFilters?.length ?? 0) > 0) {
          displayedFilters[FilterType.Object] = true;
        }
      }
    }
    this.displayedFilters = displayedFilters;
  }

  get showRecentlyUsed() {
    return this.triggersByCategory[TriggerCategory.recentlyUsed].length > 0;
  }

  get filterOptions() {
    let { shouldShowFilterControls, objectType } = this.args;
    if (shouldShowFilterControls === false) {
      return [];
    }
    let options = [
      {
        label: 'Segment filter',
        value: FilterType.Person,
        disabled: this.displayedFilters[FilterType.Person],
        action: () => this.displayFilter(FilterType.Person, true),
      },
    ];

    if (this.isObjectCampaignType && objectType != null) {
      options.push({
        label: `${FilterType.Relationship} filter`,
        value: FilterType.Relationship,
        disabled: this.displayedFilters[FilterType.Relationship],
        action: () => this.displayFilter(FilterType.Relationship, true),
      });
      options.push({
        label: `${capitalize(objectType.singularName)} filter`,
        value: FilterType.Object,
        disabled: this.displayedFilters[FilterType.Object],
        action: () => this.displayFilter(FilterType.Object, true),
      });
    }

    // case when all possible filters are added, then we return no options to hide "+ Add filter" dropdown button
    if (options.every(({ disabled }) => disabled)) {
      return [];
    }

    return options;
  }

  @action
  displayFilter(type: FilterType, value: boolean) {
    this.displayedFilters = {
      ...this.displayedFilters,
      [type]: value,
    };
  }

  get hasAnyfilters() {
    return (
      (this.args.filters ?? []).length +
        (this.args.relationshipFilters ?? []).length +
        (this.args.objectFilters ?? []).length >
      0
    );
  }

  @action
  removeFilters() {
    this.displayedFilters = {
      [FilterType.Person]: false,
      [FilterType.Relationship]: false,
      [FilterType.Object]: false,
    };
    if (this.isObjectCampaignType) {
      this.args.updateTopLevelValues({
        filters: [],
        relationshipFilters: [],
        objectFilters: [],
      });
    } else {
      this.args.updateTopLevelValue('filters', []);
    }
    this.showRemoveFiltersConfirmation = false;
  }

  get filterSidebarDescription() {
    let exitCondition = selectedExitCondition({
      exitOnTriggerOrFilterNotMatched: this.args.exitOnTriggerOrFilterNotMatched,
      exitOnConversionMatched: this.args.exitOnConversionMatched,
    });
    let filtersUsedInExitCondition =
      exitCondition === ExitCondition.UnmatchTrigger || exitCondition === ExitCondition.GoalMetOrUnmatchTrigger;

    switch (this.args.selectedType) {
      case CampaignType.Object:
      case CampaignType.ObjectRelationship: {
        if (this.args.objectType == null) {
          return '';
        }
        let objectName = article(this.args.objectType.singularName.toLowerCase());
        let action = '';
        if (this.args.selectedType === CampaignType.Object) {
          action = `${objectName} attribute changes`;
        } else {
          if (this.args.relationshipCampaign == null) {
            return '';
          }
          if (this.args.relationshipCampaign.type === ObjectRelationshipTriggerType.Created) {
            action = `a person is added to ${objectName}`;
          } else {
            action = `a person's relationship to ${objectName} is updated`;
          }
        }
        if (filtersUsedInExitCondition) {
          return `When ${action} and matches the filter conditions, they will enter the campaign. Also, before <span class="underline">any action</span> that impacts the person, we'll check to see if they still match the filter conditions`;
        }
        return `When ${action} and matches the filter conditions, they will enter the campaign.`;
      }
      case CampaignType.Transactional:
      case CampaignType.Form: {
        let action = this.args.selectedType === CampaignType.Transactional ? 'performs the event' : 'submits the form';
        if (filtersUsedInExitCondition) {
          return `When a person ${action} and matches the filter conditions, they will enter the campaign. Also, before <span class="underline">any action</span> that impacts the person, we'll check to see if they still match the filter conditions.`;
        }
        return `When a person ${action} and matches the filter conditions, they will enter the campaign.`;
      }
      case CampaignType.Behavioral:
      case CampaignType.Date: {
        if (filtersUsedInExitCondition) {
          return `Before <span class="underline">any action</span> that impacts the person, we'll check to see if they match the filter conditions.`;
        }
        return `Before <span class="underline">the first action</span> that impacts the person, we'll check to see if they match the filter conditions.`;
      }
      default: {
        return ``;
      }
    }
  }

  get filtersAreDisabled() {
    return this.args.restartMode !== CampaignRestartModes.None && this.args.selectedType === CampaignType.Behavioral;
  }

  get isObjectRelationshipCampaignType() {
    let { selectedType } = this.args;
    return CampaignType.ObjectRelationship === selectedType;
  }

  get isObjectCampaignType() {
    let { selectedType } = this.args;
    return CampaignType.Object === selectedType || this.isObjectRelationshipCampaignType;
  }

  get objectOrRelationshipCampaignSubtype() {
    if (this.isObjectRelationshipCampaignType) {
      return this.args.relationshipCampaign?.type;
    }
    if (this.isObjectCampaignType) {
      return this.args.objectCampaign?.type;
    }
    return null;
  }

  get areObjectAttributeTriggersRequired() {
    return (
      this.args.selectedType === CampaignType.Object &&
      this.objectOrRelationshipCampaignSubtype === ObjectTriggerType.Updated
    );
  }

  get areRelationshipAttributeTriggersRequired() {
    return (
      this.isObjectRelationshipCampaignType &&
      this.objectOrRelationshipCampaignSubtype === ObjectRelationshipTriggerType.Updated
    );
  }

  get isUpdateTypeCampaign() {
    return this.objectOrRelationshipCampaignSubtype === ObjectRelationshipTriggerType.Updated;
  }

  #firstAttributeConditionSentence(
    attributes: CampaignModel['relationshipAttributeTriggers'] | CampaignModel['objectAttributeTriggers'],
    useUpdateLabel?: boolean,
  ) {
    let { objectType } = this.args;
    if (objectType == null || attributes == null) {
      return '';
    }
    let [condition] = attributes;
    let operator = findOperator(condition.operator);
    if (operator != null) {
      let sentence = `${condition.field} ${operator[useUpdateLabel === true ? 'updateLabel' : 'label']}`;
      let value = getFullAttributeValue(condition);
      if (!isEmpty(value)) {
        sentence = `${sentence} ${value}`;
      }
      return sentence;
    }
    return '';
  }

  get summaryAnyActionHtml() {
    return `<a href="https://customer.io/docs/journeys/campaign-triggers-and-filters/#when-are-campaign-filters-evaluated" target="_blank" rel="noopener noreferrer">any action</a>`;
  }

  get summaryGracePeriodHtml() {
    return `<a href="https://customer.io/docs/journeys/grace-periods/#what-is-a-grace-period-and-how-does-it-work" target="_blank" rel="noopener noreferrer">grace period</a>`;
  }

  get summary() {
    let exitCondition = selectedExitCondition({
      exitOnTriggerOrFilterNotMatched: this.args.exitOnTriggerOrFilterNotMatched,
      exitOnConversionMatched: this.args.exitOnConversionMatched,
    });
    let filtersUsedInExitCondition =
      exitCondition === ExitCondition.UnmatchTrigger || exitCondition === ExitCondition.GoalMetOrUnmatchTrigger;
    let hasPersonFilters = this.args.filters?.length > 0;

    switch (this.args.selectedType) {
      case CampaignType.Behavioral: {
        let { anchors = [] } = this.args;
        if (anchors.length === 0) {
          return '';
        }
        let segmentNames = (anchors[0] as any).segments.map((segmentId: string) => {
          let segment = this.store.peekRecord('short-segment', segmentId);
          return segment?.name;
        });
        let count: number = anchors.length > 1 ? anchors.length : segmentNames.length;
        let [segmentName] = segmentNames;
        let action = anchors[0].negate === true ? 'exits' : 'enters';
        let matchPrefix = `When a person ${action} the ${segmentName} segment, they'll enter the campaign.`;
        if (count > 1) {
          matchPrefix = `When a person matches ${count} segment conditions, they'll enter the campaign.`;
        }

        if (filtersUsedInExitCondition && hasPersonFilters) {
          return `${matchPrefix} Before ${this.summaryAnyActionHtml} that impacts the person, we'll check to see if they match the segment and filter conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign after a ${this.summaryGracePeriodHtml}.`;
        }
        if (filtersUsedInExitCondition) {
          return `${matchPrefix} Before ${this.summaryAnyActionHtml} that impacts the person, we'll check to see if they still match the segment conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign after a ${this.summaryGracePeriodHtml}.`;
        }
        if (hasPersonFilters) {
          return `${matchPrefix} Before the first message is sent, we'll check to see if the person matches the segment and filter conditions. If they do, they'll receive the message. If not, they'll exit the campaign after a ${this.summaryGracePeriodHtml}.`;
        }
        return matchPrefix;
      }

      case CampaignType.Transactional: {
        let { event: eventName } = this.args;
        if (isEmpty(eventName)) {
          return '';
        }
        let matchPrefix = `When a person performs the ${eventName} event`;
        if (filtersUsedInExitCondition && hasPersonFilters) {
          return `${matchPrefix} and matches the filter conditions, they'll enter the campaign. Before ${this.summaryAnyActionHtml} that impacts the person, we'll check to see if they still match the filter conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign.`;
        }
        if (hasPersonFilters) {
          return `${matchPrefix} and matches the filter conditions, they'll enter the campaign.`;
        }
        return `${matchPrefix}, they'll enter the campaign.`;
      }

      case CampaignType.Webhook: {
        if (this.args.webhookId == null) {
          return '';
        }
        return 'Trigger the campaign when data is sent to this webhook URL.';
      }

      case CampaignType.Form: {
        let names: {
          name: string;
          extra: number;
        }[] = [];
        this.args.attributeFilters.children.forEach((condition: GroupCondition) => {
          if (isGroupCondition(condition) && condition?.children?.length > 0) {
            let formId = (condition.children.find((child: any) => child?.event === 'form_submit') as any)?.value ?? '';
            let form: CustomForm | FacebookLeadAdsForm | { name: string } = this.facebookLeadAds.forms.find(
              (f) => f.facebookFormId === formId,
            ) ??
              this.customForms.forms.find((f) => f.formIdLowercase === formId) ?? { name: formId };
            let eventConditions =
              (condition.children.find((child: any) => child?.event !== 'form_submit') as any)?.children?.length ?? 0;
            names.push({
              name: form.name,
              extra: eventConditions,
            });
          }
        });
        if (names.length === 0) {
          return '';
        }

        let count = names.reduce((total, { extra }) => total + extra + 1, 0);
        let formName = names[0].name;
        let matchPrefix = `When a person submits the ${formName} form`;
        if (count > 1) {
          matchPrefix = `When a person submits any of ${count} forms`;
        }

        if (filtersUsedInExitCondition && hasPersonFilters) {
          return `${matchPrefix} and matches the filter conditions, they'll enter the campaign. Before ${this.summaryAnyActionHtml} that impacts the person, we'll check to see if they still match the filter conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign.`;
        }
        if (hasPersonFilters) {
          return `${matchPrefix} and matches the filter conditions, they'll enter the campaign.`;
        }
        return `${matchPrefix}, they'll enter the campaign.`;
      }

      case CampaignType.Date: {
        let {
          dateTriggeredStartTime: startTime,
          dateTriggeredFrequency: frequency,
          dateTriggeredAttribute,
          dateTriggeredZone: zone,
          dateTriggeredFallbackZone: fallbackZone,
          dateTriggeredLeadDays: leadDays,
        } = this.args;
        if (
          isEmpty(dateTriggeredAttribute) ||
          isEmpty(frequency) ||
          isEmpty(startTime) ||
          (isEmpty(zone) && isEmpty(fallbackZone))
        ) {
          return '';
        }

        let values = [];
        if (frequency === FrequencyOption.yearly) {
          values.push('every year');
        } else if (frequency === FrequencyOption.monthly) {
          values.push('every month');
        } else {
          values.push('once');
        }
        if (leadDays > 0) {
          values.push(`${leadDays} ${pluralize(leadDays, 'day')} after`);
        } else if (leadDays < 0) {
          values.push(`${-leadDays} ${pluralize(-leadDays, 'day')} before`);
        } else {
          values.push('on');
        }
        let dateTrigger = `${values.join(' ')} the ${dateTriggeredAttribute} date at ${startTime} ${
          isEmpty(zone) ? fallbackZone : zone
        }.`;

        if (filtersUsedInExitCondition && hasPersonFilters) {
          return `People matching the filter conditions will enter this campaign ${dateTrigger}  Before ${this.summaryAnyActionHtml} that impacts the person, we'll check to see if they still match the filter conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign.`;
        }
        if (hasPersonFilters) {
          return `People matching the filter conditions will enter this campaign ${dateTrigger}`;
        }
        return `People will enter this campaign ${dateTrigger}`;
      }

      case CampaignType.Object: {
        let { objectAttributeTriggers = [], objectType } = this.args;
        if (objectType == null) {
          return '';
        }
        let count = objectAttributeTriggers?.length ?? 0;
        if (this.objectOrRelationshipCampaignSubtype === ObjectTriggerType.Updated) {
          if (objectAttributeTriggers == null || count < 1) {
            return '';
          }
          if (count === 1) {
            let sentence = this.#firstAttributeConditionSentence(objectAttributeTriggers, true);
            if (sentence != null) {
              return `Trigger when ${article(objectType.singularName.toLowerCase())}'s attribute ${sentence}. ${
                this.summaryAudience
              }`;
            }
          }
          return `Trigger when ${count} ${objectType.singularName.toLowerCase()} attributes change to match the conditions. ${
            this.summaryAudience
          }`;
        }
        // for now other Object types are disabled
        return '';
      }

      case CampaignType.ObjectRelationship: {
        let { objectType, relationshipAttributeTriggers = [] } = this.args;
        if (objectType == null) {
          return '';
        }
        let count = relationshipAttributeTriggers?.length ?? 0;
        let anObjectName = article(objectType.singularName.toLowerCase());
        switch (this.objectOrRelationshipCampaignSubtype) {
          case ObjectRelationshipTriggerType.Created: {
            if (count === 0) {
              return `Trigger when any person is added to ${anObjectName}. ${this.summaryAudience}`;
            }
            return `Trigger when a person is added to ${anObjectName} and they match the relationship ${pluralize(
              count,
              'condition',
            )}. ${this.summaryAudience}`;
          }
          case ObjectRelationshipTriggerType.Updated: {
            if (count === 0) {
              return '';
            }
            if (count === 1) {
              let sentence = this.#firstAttributeConditionSentence(relationshipAttributeTriggers, true);
              if (sentence != null) {
                return `Trigger when relationship attribute ${sentence}. ${this.summaryAudience}`;
              }
            }
            return `Tigger when ${count} relationship attributes change to match the conditions. ${this.summaryAudience}`;
          }
          case ObjectRelationshipTriggerType.Deleted: // currently disabled type
          default: {
            return '';
          }
        }
      }
      default: {
        return '';
      }
    }
  }

  get summaryAudience() {
    let {
      audience,
      selectedType,
      objectType,
      objectCampaign,
      relationshipCampaign,
      exitOnTriggerOrFilterNotMatched,
      exitOnConversionMatched,
      filters,
      relationshipFilters,
      objectFilters,
    } = this.args;
    if (audience == null || objectType == null) {
      return '';
    } else {
      let exitCondition = selectedExitCondition({
        exitOnTriggerOrFilterNotMatched,
        exitOnConversionMatched,
      });
      let filtersUsedInExitCondition =
        exitCondition === ExitCondition.UnmatchTrigger || exitCondition === ExitCondition.GoalMetOrUnmatchTrigger;
      let hasPersonFilters = filters?.length > 0;
      let hasRelationshipFilters = (relationshipFilters ?? []).length > 0;
      let hasObjectFilters = (objectFilters ?? []).length > 0;
      let hasAnyFilters = hasPersonFilters || hasRelationshipFilters || hasObjectFilters;
      let action = '';

      // Check for currently non-existing Object/Relationship types and do setup, or return for non existing cases
      if (selectedType === CampaignType.Object && objectCampaign != null) {
        if (objectCampaign.type === ObjectTriggerType.Updated && audience.type !== AudienceSettingsType.self) {
          action = 'When changed,';
        } else {
          return ''; // end early for non-existing subtypes
        }
      } else if (selectedType === CampaignType.ObjectRelationship && relationshipCampaign != null) {
        if (relationshipCampaign.type === ObjectRelationshipTriggerType.Created) {
          action = 'When added,';
        } else if (relationshipCampaign.type === ObjectRelationshipTriggerType.Updated) {
          if (audience.type === AudienceSettingsType.self) {
            action = 'When their relationship changes,';
          } else {
            action = 'When the relationship changes,';
          }
        } else {
          return ''; // end early for non-existing subtypes
        }
      } else {
        return ''; // end early for non-existing types
      }

      let personRelatedText = `every person related to the ${objectType.singularName.toLowerCase()}`;

      switch (audience.type) {
        case AudienceSettingsType.self: {
          if (filtersUsedInExitCondition && hasAnyFilters) {
            return `${action} they will enter the campaign if they match the filter conditions. Before ${this.summaryAnyActionHtml} that impacts the person, we'll check to see if they still match the filter conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign.`;
          }
          if (hasAnyFilters) {
            return `${action} they will enter the campaign if they match the filter conditions.`;
          }
          return `${action} they will enter the campaign.`;
        }
        case AudienceSettingsType.related: {
          if (filtersUsedInExitCondition && hasAnyFilters) {
            return `${action} ${personRelatedText} matching the filter conditions will enter the campaign. Before ${this.summaryAnyActionHtml} that impacts a person, we'll check to see if they still match the filter conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign.`;
          }
          if (hasAnyFilters) {
            return `${action} ${personRelatedText} matching the filter conditions will enter the campaign.`;
          }
          return `${action} ${personRelatedText} will enter the campaign.`;
        }
        case AudienceSettingsType.filtered: {
          if (filtersUsedInExitCondition && hasAnyFilters) {
            return `${action} ${personRelatedText} matching the audience and filter conditions will enter the campaign. Before ${this.summaryAnyActionHtml} that impacts a person, we'll check to see if they still match the filter conditions. If they do, they'll continue in the workflow. If not, they'll exit the campaign.`;
          }
          if (hasAnyFilters) {
            return `${action} ${personRelatedText} matching the audience and filter conditions will enter the campaign.`;
          }
          return `${action} ${personRelatedText} matching the audience conditions will enter the campaign.`;
        }
        default: {
          return '';
        }
      }
    }
  }

  get summaryFrequency() {
    let { selectedType } = this.args;

    if (this.args.shouldShowFrequencyControls) {
      let allFreqOptions = EventAndFormFrequencyOptions;
      if (selectedType === CampaignType.Behavioral) {
        allFreqOptions = SegmentFrequencyOptions;
      } else if (selectedType === CampaignType.Object || selectedType === CampaignType.ObjectRelationship) {
        allFreqOptions = ObjectAndRelationshipFrequencyOptions;
      }
      let freqOpt = getSelectedFrequencyOption(
        this.args.selectedType,
        allFreqOptions,
        this.args.restartMode,
        this.args.restartMinInterval,
      );
      if (freqOpt != null && !isEmpty(freqOpt.summary)) {
        return freqOpt.summary;
      }
    }
    return '';
  }

  get summaries() {
    if (isEmpty(this.summary)) {
      return [];
    }
    let paragraphs = [
      {
        text: this.summary,
        id: 'trigger',
      },
    ];

    if (this.summaryFrequency != null && this.summaryFrequency !== '') {
      paragraphs.push({
        text: this.summaryFrequency,
        id: 'frequency',
      });
    }

    return paragraphs;
  }

  get visibleColumnCount() {
    let {
      anchors,
      relationshipAttributeTriggers,
      objectAttributeTriggers,
      filters,
      relationshipFilters,
      objectFilters,
    } = this.args;

    return Math.min(
      this.maxColumnCountBeforeThreshold,
      Math.max(
        0,
        ...[
          anchors,
          relationshipAttributeTriggers,
          objectAttributeTriggers,
          filters,
          relationshipFilters,
          objectFilters,
        ].map((items) => (items ?? []).length),
      ),
    );
  }

  get selectedTypeSuffix() {
    let { selectedType, objectType, relationshipCampaign } = this.args;
    if (selectedType === CampaignType.ObjectRelationship && objectType != null && relationshipCampaign != null) {
      switch (relationshipCampaign.type) {
        case ObjectRelationshipTriggerType.Created: {
          return `to ${objectType.singularName}`;
        }
        case ObjectRelationshipTriggerType.Updated: {
          return `with ${objectType.singularName}`;
        }
        case ObjectRelationshipTriggerType.Deleted: {
          return `from ${objectType.singularName}`;
        }
      }
    }
    return '';
  }

  @action
  setSelectedCategory(value: string) {
    this.selectedCategory = value ?? TriggerCategory.recentlyUsed;
    if (!isEmpty(this.searchTerm?.trim())) {
      this.searchTerm = '';
      this.filterTypesForSearch();
    } else {
      this.analytics.track('campaign', 'trigger_filtered', {
        selectedView:
          TRIGGER_CATEGORIES_NAMES_FOR_TRACKING[this.selectedCategory as TriggerCategory] ??
          TRIGGER_CATEGORIES_NAMES_FOR_TRACKING[TriggerCategory.objects], // category is object_type_{id}, but we don't need the id
      });
    }
  }

  @action
  filterTypesForSearch() {
    let term = this.searchTerm?.trim().toLowerCase();
    if (isEmpty(term)) {
      this.triggersByCategoryFiltered = cloneDeep(this.triggersByCategory);
    } else {
      let filteredTriggers = Object.entries(this.triggersByCategory).reduce<typeof TriggersByCategory>(
        (acc, [category, items]) => {
          if (category === TriggerCategory.recentlyUsed) {
            return acc;
          }
          let filteredItems = items.filter(
            (item) =>
              item.label.toLowerCase().includes(term) ||
              item.description.toLowerCase().includes(term) ||
              item.tags?.some((tag) => tag.toLowerCase().includes(term)),
          );
          if (filteredItems.length > 0) {
            acc[category] = filteredItems;
          }
          return acc;
        },
        {},
      );
      let filteredCategories = Object.keys(filteredTriggers);
      this.selectedCategory = filteredCategories.length === 1 ? filteredCategories[0] : TriggerCategory.all;
      this.triggersByCategoryFiltered = filteredTriggers;
      this.analytics.track('campaign', 'trigger_filtered', {
        selectedView:
          TRIGGER_CATEGORIES_NAMES_FOR_TRACKING[this.selectedCategory as TriggerCategory] ??
          TRIGGER_CATEGORIES_NAMES_FOR_TRACKING[TriggerCategory.objects], // category is object_type_{id}, but we don't need the id
        searchTerm: term,
      });
    }
  }

  @action
  search(value: string) {
    this.searchTerm = value.trim();
    debounce(this, this.filterTypesForSearch, config.APP.shortDelay);
  }

  @action
  onChangeType(
    type: CampaignType,
    label: string,
    objectTypeId?: string,
    subType?: ObjectTriggerType | ObjectRelationshipTriggerType,
    trackingValue?: string,
  ) {
    this.args.onChangeType(type, objectTypeId, subType, trackingValue);
    this.selectedTypeName = label ?? '';
  }

  @action
  changeSelectedType() {
    if (this.hasTriggerConditions) {
      this.showConditionResetConfirmation = true;
    } else {
      this.args.resetSelectedType();
    }
    this.displayedFilters = {
      [FilterType.Person]: false,
      [FilterType.Relationship]: false,
      [FilterType.Object]: false,
    };
    this.showRemoveFiltersConfirmation = false;
    this.showAllSummaries = false;
  }

  @action
  resetConditions() {
    this.showConditionResetConfirmation = false;
    let { campaign } = this.args;
    let attributeFilters = campaign.isFormTriggered
      ? deserializeFormAttributeFilters(campaign.attributeFilters)
      : deserializeEventAttributeFilters(campaign.attributeFilters, campaign.event);
    this.args.updateTopLevelValues({
      filters: [],
      anchors: [],
      event: null,
      attributeFilters,
      dateTriggeredFrequency: null,
      dateTriggeredLeadDays: null,
      dateTriggeredStartTime: null,
      dateTriggeredAttribute: null,
      dateTriggeredZone: null,
      dateTriggeredFallbackZone: null,
      restartMode: CampaignRestartModes.None,
      restartMinInterval: 0,
    });
    this.args.resetSelectedType();
  }
}
